import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../api"; // ✅ axios instance


// Import Components
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import PageTitle from "../components/PageTitleCompoent";
import SubscriptionDashboard from "../components/SubscriptionDashboard";
import DeleteWithConfirmation from "../components/DeleteWithConfirmation";

// Import CSS & Assets
import "../css/SubscriptionPlanScreen.css";
import ribbonYearly from "../assets/ribbon-yearly.png";
import ribbonHalfYearly from "../assets/ribbon-half-yearly.png";
import ribbonQuarterly from "../assets/ribbon-half-yearly.png";
import ribbonMonthly from "../assets/ribbon-yearly.png";

// ✅ Fixed duration mapping keys (match backend response)
const durationMapping = {
  yearly: "Yearly",
  half_yearly: "Half Yearly",
  quarterly: "Quarterly",
  monthly: "Monthly",
};

// ✅ Fixed ribbon mapping keys (match backend response)
const ribbonImages = {
  yearly: ribbonYearly,
  half_yearly: ribbonHalfYearly,
  quarterly: ribbonQuarterly,
  monthly: ribbonMonthly,
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 120 },
  },
};

export default function SubscriptionPlanScreen() {
  const navigate = useNavigate();
  const location = useLocation();

  const [plans, setPlans] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState({
    id: null,
    type: null,
    name: "",
  });

  // ✅ Fetch subscription data from backend
  const fetchPlans = async () => {
    try {
      const res = await api.get("/subscription-plans/", {
        params: {
          subscription_duration: "half_yearly", // default filter
        },
      });
      const data = res.data;

      setPlans(data.subscription_plans || []);
      const allSubs = data.subscription_plans.flatMap(
        (plan) => plan.my_subscribers || []
      );
      setSubscribers(allSubs);
    } catch (err) {
      console.error("Failed to fetch subscription plans:", err);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // ✅ Handle new plan added via Add Subscription screen
  useEffect(() => {
    const receivedData = location.state?.formData;
    if (receivedData) {
      setPlans((prevPlans) => {
        const exists = prevPlans.some(
          (plan) =>
            plan.subscription_duration.toLowerCase() ===
            receivedData.type.toLowerCase()
        );
        if (exists) {
          console.warn("A plan of this type already exists. Not adding duplicate.");
          return prevPlans;
        }
        // If backend should persist, you’d POST here instead of local add
        const newPlan = {
          ...receivedData,
          id: `temp-${Date.now()}`,
          features: receivedData.features || [],
        };
        return [...prevPlans, newPlan];
      });

      navigate(".", { replace: true, state: {} });
    }
  }, [location, navigate]);

  // ✅ Delete modal handlers
  const handleOpenDeleteModal = (id, type, name) => {
    setItemToDelete({ id, type, name });
    setIsModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsModalOpen(false);
    setItemToDelete({ id: null, type: null, name: "" });
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete.type === "plan") {
      try {
        await api.delete(`/subscription-plans/${itemToDelete.id}/`);
        setPlans((current) =>
          current.filter((p) => p.id !== itemToDelete.id)
        );
      } 
      catch (err) {
        console.error("Failed to delete subscription plan:", err);
      }
    }
    handleCloseDeleteModal();
  };

  const handleBack = () => navigate(-1);

  return (
    <div className="subscription-plan-container">
      <Sidebar />
      <div className="subscription-plan-main">
        <Header />
        <motion.div
          className="subscription-plan-content"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            variants={itemVariants}
            className="subscription-plan-heading-row"
          >
            <PageTitle title="Subscription Plans"  />
            <div className="subscription-plan-actions">
              <motion.button
                onClick={() => navigate("/add-subscription")}
                className="add-plan-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={20}
                  height={20}
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="#FFFFFF"
                    d="M19 12.998h-6v6h-2v-6H5v-2h6v-6h2v6h6z"
                  />
                </svg>
                <span className="add-plan-text">Add Subscription Plan</span>
              </motion.button>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="subscription-plan-grid">
            <SubscriptionDashboard
              plans={plans}
              subscribers={subscribers}
              durationMap={durationMapping}
              ribbonMap={ribbonImages}
              onDeleteRequest={handleOpenDeleteModal}
            />
          </motion.div>
        </motion.div>
      </div>

      <DeleteWithConfirmation
        isOpen={isModalOpen}
        onCancel={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title={`Are you sure you want to delete the "${itemToDelete.name}" plan?`}
        message="This action cannot be undone."
      />
    </div>
  );
}
